import { useId } from "react";
import {
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DemographicLimits } from "@/features/HealthCheck/types";

const inputFieldStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
  },
  "& input": {
    textAlign: "center",
    fontSize: 18,
    fontWeight: 500,
    width: 80,
  },
  "& input::placeholder": {
    fontSize: 16,
  },
};

const inputFieldStylesFullWidth = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
  },
  "& input": {
    textAlign: "center",
    fontSize: 18,
    fontWeight: 500,
  },
  "& input::placeholder": {
    fontSize: 16,
  },
  flex: 1,
};

const unitStyles = {
  fontSize: 20,
  fontWeight: 600,
  color: "neutral.900",
};

function ErrorSlot({
  hasError,
  errorText,
  id,
}: {
  hasError: boolean;
  errorText?: string;
  id: string;
}) {
  return (
    <FormHelperText sx={{ mx: 0, minHeight: 32 }} error={hasError} id={id}>
      {hasError && errorText ? errorText : "\u00A0"}
    </FormHelperText>
  );
}

/**
 * Check whether a vital input value is invalid (out of range)
 *
 * @param value - The raw string value from the input
 * @param min - Minimum allowed value
 * @param max - Maximum allowed value
 * @returns true when the value is non-empty and outside [min, max]
 */
export function getVitalInputError(
  value: string,
  min: number,
  max: number
): boolean {
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "-") return false;
  const num = Number(trimmed);
  if (Number.isNaN(num)) return false;
  return num < min || num > max;
}

/**
 * Sanitize raw vital input: reject NaN and strip decimals when integerOnly
 *
 * @returns an object with rejected: true if the input should be rejected,
 *          or an object with the sanitized value and rejected: false otherwise
 */
function sanitizeVitalInput(
  raw: string,
  integerOnly: boolean
): { value: string; rejected: false } | { rejected: true } {
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed === "-")
    return { value: trimmed, rejected: false };
  if (integerOnly && trimmed.includes(".")) return { rejected: true };
  const num = Number(trimmed);
  if (Number.isNaN(num)) return { rejected: true };
  return { value: trimmed, rejected: false };
}

export interface SingleVitalInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel: string;
  unit: string;
  min?: number;
  max?: number;
  fullWidth?: boolean;
  integerOnly?: boolean;
  errorText?: string;
}

export function SingleVitalInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
  unit,
  min = 0,
  max = 300,
  fullWidth = false,
  integerOnly = false,
  errorText,
}: SingleVitalInputProps) {
  const helperId = useId();

  const handleChange = (raw: string) => {
    const result = sanitizeVitalInput(raw, integerOnly);
    if (result.rejected) return;
    if (integerOnly && max != null) {
      const maxDigits = String(Math.floor(max)).length;
      const normalizedForLength = result.value.replace(
        /^([-+])?0+(?=\d)/,
        "$1"
      );
      const valueWithoutSign = normalizedForLength.replace(/^[+-]/, "");
      if (valueWithoutSign.length > maxDigits) return;
    }
    onChange(result.value);
  };

  const hasError = getVitalInputError(value, min, max);

  return (
    <Stack direction="column" gap={0.5}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        sx={fullWidth ? { width: "100%" } : undefined}
      >
        <TextField
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          type="number"
          placeholder={placeholder}
          error={hasError}
          slotProps={{
            htmlInput: {
              "aria-label": ariaLabel,
              "aria-describedby": hasError && errorText ? helperId : undefined,
              min,
              max,
            },
          }}
          sx={fullWidth ? inputFieldStylesFullWidth : inputFieldStyles}
        />
        <Typography sx={unitStyles}>{unit}</Typography>
      </Stack>
      <ErrorSlot hasError={hasError} errorText={errorText} id={helperId} />
    </Stack>
  );
}

const HEIGHT_UNITS = [
  { value: "cm", label: "cm" },
  { value: "m", label: "m" },
] as const;

export interface VitalInputWithUnitSelectProps {
  value: string;
  onChange: (value: string) => void;
  unit: "cm" | "m";
  onUnitChange: (unit: "cm" | "m") => void;
  placeholder: string;
  ariaLabel: string;
  min?: number;
  max?: number;
  errorText?: string;
}

export function VitalInputWithUnitSelect({
  value,
  onChange,
  unit,
  onUnitChange,
  placeholder,
  ariaLabel,
  min = DemographicLimits.MIN_ALLOWED_HEIGHT,
  max = DemographicLimits.MAX_ALLOWED_HEIGHT,
  errorText,
}: VitalInputWithUnitSelectProps) {
  const helperId = useId();
  const minForUnit = unit === "m" ? min / 100 : min;
  const maxForUnit = unit === "m" ? max / 100 : max;

  const handleChange = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === "" || trimmed === "-") {
      onChange(trimmed);
      return;
    }
    const num = Number(trimmed);
    if (Number.isNaN(num)) return;
    const valueCm = unit === "m" ? num * 100 : num;
    if (valueCm > max) {
      const clampedForDisplay =
        unit === "m" ? String((max / 100).toFixed(2)) : String(max);
      onChange(clampedForDisplay);
      return;
    }
    onChange(trimmed);
  };

  const valueCm = (() => {
    const trimmed = value.trim();
    if (trimmed === "" || trimmed === "-") return NaN;
    const num = Number(trimmed);
    return unit === "m" ? num * 100 : num;
  })();
  const hasError =
    !Number.isNaN(valueCm) && getVitalInputError(String(valueCm), min, max);

  return (
    <Stack direction="column" gap={0.5} sx={{ width: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        sx={{ width: "100%" }}
      >
        <TextField
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          type="number"
          placeholder={placeholder}
          error={hasError}
          slotProps={{
            htmlInput: {
              "aria-label": ariaLabel,
              "aria-describedby": hasError && errorText ? helperId : undefined,
              min: minForUnit,
              max: maxForUnit,
              step: unit === "m" ? 0.01 : 1,
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
            "& input": {
              textAlign: "center",
              fontSize: 18,
              fontWeight: 500,
            },
            "& input::placeholder": {
              fontSize: 16,
            },
            flex: 1,
          }}
        />
        <Select
          value={unit}
          onChange={(e) => onUnitChange(e.target.value as "cm" | "m")}
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
            "& .MuiSelect-select": {
              fontSize: 20,
              fontWeight: 600,
            },
          }}
          inputProps={{
            "aria-label": `${ariaLabel} unit`,
          }}
        >
          {HEIGHT_UNITS.map(({ value: v, label }) => (
            <MenuItem key={v} value={v}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </Stack>
      <ErrorSlot hasError={hasError} errorText={errorText} id={helperId} />
    </Stack>
  );
}

export interface BloodPressureInputProps {
  systolic: string;
  diastolic: string;
  onSystolicChange: (value: string) => void;
  onDiastolicChange: (value: string) => void;
  systolicLabel: string;
  diastolicLabel: string;
  unit: string;
  systolicMin?: number;
  systolicMax?: number;
  diastolicMin?: number;
  diastolicMax?: number;
  integerOnly?: boolean;
  systolicErrorText?: string;
  diastolicErrorText?: string;
}

export function BloodPressureInput({
  systolic,
  diastolic,
  onSystolicChange,
  onDiastolicChange,
  systolicLabel,
  diastolicLabel,
  unit,
  systolicMin = 0,
  systolicMax = 300,
  diastolicMin = 0,
  diastolicMax = 200,
  integerOnly = false,
  systolicErrorText,
  diastolicErrorText,
}: BloodPressureInputProps) {
  const systolicHelperId = useId();
  const diastolicHelperId = useId();

  const handleSystolicChange = (raw: string) => {
    const result = sanitizeVitalInput(raw, integerOnly);
    if (!result.rejected) onSystolicChange(result.value);
  };

  const handleDiastolicChange = (raw: string) => {
    const result = sanitizeVitalInput(raw, integerOnly);
    if (!result.rejected) onDiastolicChange(result.value);
  };

  const systolicHasError = getVitalInputError(
    systolic,
    systolicMin,
    systolicMax
  );
  const diastolicHasError = getVitalInputError(
    diastolic,
    diastolicMin,
    diastolicMax
  );

  return (
    <Stack direction="row" alignItems="flex-start" gap={1.5}>
      <Stack
        direction="column"
        gap={0.5}
        sx={{ minWidth: 0, maxWidth: 112, flexShrink: 0 }}
      >
        <TextField
          value={systolic}
          onChange={(e) => handleSystolicChange(e.target.value)}
          type="number"
          placeholder="120"
          error={systolicHasError}
          slotProps={{
            htmlInput: {
              "aria-label": systolicLabel,
              "aria-describedby":
                systolicHasError && systolicErrorText
                  ? systolicHelperId
                  : undefined,
              min: systolicMin,
              max: systolicMax,
            },
          }}
          sx={inputFieldStyles}
        />
        <ErrorSlot
          hasError={!!(systolicHasError && systolicErrorText)}
          errorText={systolicErrorText}
          id={systolicHelperId}
        />
      </Stack>
      <Typography
        sx={{
          fontSize: 24,
          fontWeight: 500,
          color: "neutral.700",
          pt: 1.25,
          flexShrink: 0,
        }}
      >
        /
      </Typography>
      <Stack
        direction="column"
        gap={0.5}
        sx={{ minWidth: 0, maxWidth: 112, flexShrink: 0 }}
      >
        <TextField
          value={diastolic}
          onChange={(e) => handleDiastolicChange(e.target.value)}
          type="number"
          placeholder="80"
          error={diastolicHasError}
          slotProps={{
            htmlInput: {
              "aria-label": diastolicLabel,
              "aria-describedby":
                diastolicHasError && diastolicErrorText
                  ? diastolicHelperId
                  : undefined,
              min: diastolicMin,
              max: diastolicMax,
            },
          }}
          sx={inputFieldStyles}
        />
        <ErrorSlot
          hasError={!!(diastolicHasError && diastolicErrorText)}
          errorText={diastolicErrorText}
          id={diastolicHelperId}
        />
      </Stack>
      <Typography sx={{ ...unitStyles, ml: 1, pt: 1.25, flexShrink: 0 }}>
        {unit}
      </Typography>
    </Stack>
  );
}
